import { supabase } from '../lib/supabase';
import type { YoutubeSearchResult } from '../models/playlist.model';

export interface BuscarVideosOpts {
  maxResults?: number;
  telaId?: string;
}

export interface ImportarPlaylistOpts {
  telaId?: string;
}

function extractPlaylistId(input: string): string | null {
  const trimmed = input.trim();
  const fromUrl = trimmed.match(/[?&]list=([a-zA-Z0-9_-]+)/);
  if (fromUrl) return fromUrl[1];
  if (/^[A-Z]{2}[a-zA-Z0-9_-]{10,}$/.test(trimmed)) return trimmed;
  return null;
}

async function invokeYoutubeProxy(
  body: Record<string, unknown>,
): Promise<YoutubeSearchResult[]> {
  const { data, error } = await supabase.functions.invoke('youtube-proxy', { body });

  if (error) {
    const fromBody =
      data && typeof data === 'object' && !Array.isArray(data) && 'error' in data
        ? String((data as { error: unknown }).error)
        : null;
    throw new Error(fromBody ?? error.message ?? 'Falha ao chamar youtube-proxy');
  }

  if (data && typeof data === 'object' && !Array.isArray(data) && 'error' in data) {
    throw new Error(String((data as { error: unknown }).error));
  }

  if (!Array.isArray(data)) return [];
  return data as YoutubeSearchResult[];
}

export const youtubeService = {
  /**
   * Pesquisa vídeos via Edge Function `youtube-proxy` (secret YOUTUBE_API_KEY no servidor).
   * Jukebox anon deve passar `telaId`; admin com JWT tenant pode omitir.
   */
  async buscarVideos(
    query: string,
    opts?: BuscarVideosOpts,
  ): Promise<YoutubeSearchResult[]> {
    if (!query.trim()) return [];

    return invokeYoutubeProxy({
      action: 'search',
      query: query.trim(),
      ...(opts?.maxResults != null ? { maxResults: opts.maxResults } : {}),
      ...(opts?.telaId ? { tela_id: opts.telaId } : {}),
    });
  },

  /**
   * Importa playlist pública via `youtube-proxy`. Aceita URL com list=… ou ID bare.
   */
  async importarPlaylist(
    input: string,
    opts?: ImportarPlaylistOpts,
  ): Promise<YoutubeSearchResult[]> {
    const playlistId = extractPlaylistId(input);
    if (!playlistId) {
      throw new Error(
        'Link inválido — cole uma URL do YouTube contendo "list=PL…" ' +
        'ou o ID direto da playlist',
      );
    }

    return invokeYoutubeProxy({
      action: 'importPlaylist',
      input: input.trim(),
      ...(opts?.telaId ? { tela_id: opts.telaId } : {}),
    });
  },
};
